/**
 * Consent Ledger React hooks – Phase 3B.
 *
 * Wraps the consent service in React Query hooks so components get automatic
 * loading / error states, caching, background refetching, and mutations with
 * optimistic invalidation.
 *
 * All hooks are designed to degrade gracefully: if the backend is down the
 * service layer returns mock data so the UI stays functional.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getActiveConsents,
  getConsentHistory,
  getConsentRequests,
  grantConsent,
  denyConsent,
  revokeConsent,
  verifyChain,
} from '../services/consent-service';
import type {
  DenyConsentPayload,
  GrantConsentPayload,
  RevokeConsentPayload,
} from '../types/consent-types';

// ---------------------------------------------------------------------------
// Query key factory – keeps keys co-located and type-safe.
// ---------------------------------------------------------------------------

export const consentKeys = {
  all: (patientId: string) => ['consent', patientId] as const,
  requests: (patientId: string) => ['consent', patientId, 'requests'] as const,
  history: (patientId: string) => ['consent', patientId, 'history'] as const,
  active: (patientId: string) => ['consent', patientId, 'active'] as const,
  verification: (patientId: string) =>
    ['consent', patientId, 'verify'] as const,
};

// ---------------------------------------------------------------------------
// Pending requests
// ---------------------------------------------------------------------------

/**
 * Return pending consent requests for *patientId*, polling every 30 seconds
 * so new provider requests surface without a manual refresh.
 *
 * @param patientId - Supabase UUID string; pass ``null`` to skip the query.
 */
export function useConsentRequests(patientId: string | null) {
  return useQuery({
    queryKey: consentKeys.requests(patientId ?? ''),
    queryFn: () => getConsentRequests(patientId!),
    enabled: patientId != null,
    refetchInterval: 30_000, // poll every 30 s
    staleTime: 20_000,
  });
}

// ---------------------------------------------------------------------------
// Full audit trail
// ---------------------------------------------------------------------------

/**
 * Return the complete consent event chain for *patientId*.
 *
 * @param patientId - Supabase UUID string; pass ``null`` to skip the query.
 */
export function useConsentHistory(patientId: string | null) {
  return useQuery({
    queryKey: consentKeys.history(patientId ?? ''),
    queryFn: () => getConsentHistory(patientId!),
    enabled: patientId != null,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Active consents
// ---------------------------------------------------------------------------

/**
 * Return all currently active grant events for *patientId*.
 *
 * @param patientId - Supabase UUID string; pass ``null`` to skip the query.
 */
export function useActiveConsents(patientId: string | null) {
  return useQuery({
    queryKey: consentKeys.active(patientId ?? ''),
    queryFn: () => getActiveConsents(patientId!),
    enabled: patientId != null,
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Chain integrity verification
// ---------------------------------------------------------------------------

/**
 * Verify the integrity of the patient's consent chain.
 * Verification is not automatically refetched (user-triggered via a button).
 *
 * @param patientId - Supabase UUID string; pass ``null`` to skip.
 */
export function useConsentVerification(patientId: string | null) {
  return useQuery({
    queryKey: consentKeys.verification(patientId ?? ''),
    queryFn: () => verifyChain(patientId!),
    enabled: patientId != null,
    staleTime: Infinity, // re-verify only on explicit refetch
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/**
 * Mutation: grant a consent request.
 * Invalidates the requests, history, and active caches on success.
 */
export function useGrantConsent(patientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: GrantConsentPayload) => grantConsent(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: consentKeys.requests(patientId) });
      qc.invalidateQueries({ queryKey: consentKeys.history(patientId) });
      qc.invalidateQueries({ queryKey: consentKeys.active(patientId) });
    },
  });
}

/**
 * Mutation: deny a consent request.
 * Invalidates the requests and history caches on success.
 */
export function useDenyConsent(patientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: DenyConsentPayload) => denyConsent(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: consentKeys.requests(patientId) });
      qc.invalidateQueries({ queryKey: consentKeys.history(patientId) });
    },
  });
}

/**
 * Mutation: revoke an existing grant.
 * Invalidates the active and history caches on success.
 */
export function useRevokeConsent(patientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      payload,
    }: {
      eventId: string;
      payload: RevokeConsentPayload;
    }) => revokeConsent(eventId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: consentKeys.active(patientId) });
      qc.invalidateQueries({ queryKey: consentKeys.history(patientId) });
    },
  });
}
